import { useQuery } from "@tanstack/react-query"
import * as React from "react"
import { Outlet, useLocation, useNavigate } from "react-router"

import {
  AppShell,
  PROFILE_HREF,
  activeNavId,
  navFor,
  type NavGroupDef,
  type NavRole,
} from "@/components/shell"
import { RequireAuth, useCurrentUser, useLogoutMutation } from "@/features/auth"
import { threadsSummaryQuery, UNFILTERED_SUMMARY } from "@/features/queue"
import { Role, ROLE_LABEL, toRole } from "@/lib/roles"
import { useTheme } from "@/lib/use-theme"

// Wraps every route with AppShell and owns app-level concerns (theme, nav map,
// current href, sign-out) so route components don't have to. Guarded by
// RequireAuth so an unauth reviewer is bounced to /login before this shell
// tries to render any data.

export function ShellRoute() {
  return (
    <RequireAuth>
      <AuthenticatedShell />
    </RequireAuth>
  )
}

/**
 * The signed-in shell around `children`, or around the matched route when there are none.
 * Exported for the 404, which is matched outside the shell's routes (so it is answered
 * before any sign-in check) but still wants the sidebar when someone is signed in. The
 * caller must already know a session exists; `ShellRoute` is the guarded entry point.
 */
export function SignedInShell({
  children,
  title,
  unknownRoute = false,
}: {
  children?: React.ReactNode
  /** Overrides the tab title, for a page that is not a nav item. */
  title?: string
  /** True for the 404 page: a dead link must not highlight a nav item or write sidebar prefs. */
  unknownRoute?: boolean
} = {}): JSX.Element {
  return (
    <AuthenticatedShell title={title} unknownRoute={unknownRoute}>
      {children}
    </AuthenticatedShell>
  )
}

function AuthenticatedShell({
  children,
  title,
  unknownRoute = false,
}: {
  children?: React.ReactNode | undefined
  title?: string | undefined
  unknownRoute?: boolean | undefined
} = {}): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const currentUser = useCurrentUser()
  const { logout } = useLogoutMutation()
  // The badge counts work that is still open, so a cleared queue shows nothing
  // at all — a zero would read as a number worth checking. The unfiltered
  // summary: whatever the queue page is filtered to, the badge is the queue.
  const { data: counts } = useQuery(threadsSummaryQuery(UNFILTERED_SUMMARY))

  const currentHref = location.pathname

  // Unknown role → viewer (safest non-client fallback for an authenticated staff user).
  const role: NavRole = (currentUser && toRole(currentUser.role)) ?? Role.VIEWER
  // A client signs in to its own map ("Your reviews → Label reviews"), never the staff one.
  const baseNav = navFor(role)

  // A 404 under /reviews/... should not trigger the workspace rail or persist a collapse.
  const activeId = unknownRoute ? "" : activeNavId(currentHref, baseNav)
  const appHref = unknownRoute ? "/" : currentHref

  const routeTitle = title ?? titleFor(currentHref, activeId, baseNav)
  const user = {
    name: currentUser?.name ?? currentUser?.email ?? "",
    email: currentUser?.email ?? "",
    roleLabel: ROLE_LABEL[role],
  }

  const open = counts ? counts.queued + counts.in_review + counts.needs_sme : undefined
  const nav = React.useMemo(
    () =>
      baseNav.map((group) => ({
        ...group,
        items: group.items.map((item) =>
          item.id === "queue" ? { ...item, badge: open || undefined } : item,
        ),
      })),
    [baseNav, open],
  )

  return (
    <AppShell
      nav={nav}
      role={role}
      activeId={activeId}
      routeTitle={routeTitle}
      currentHref={appHref}
      user={user}
      theme={theme}
      onThemeChange={setTheme}
      onNavigate={(href) => {
        void navigate(href)
      }}
      onSignOut={() => {
        logout()
        void navigate("/login", { replace: true })
      }}
    >
      {children ?? <Outlet />}
    </AppShell>
  )
}

function titleFor(href: string, activeId: string, nav: readonly NavGroupDef[]): string {
  const item = nav.flatMap((g) => g.items).find((i) => i.id === activeId)
  if (item) return item.label
  if (href === PROFILE_HREF) return "Your account"
  return "Pyxis"
}
